<script setup lang="ts">
import { computed, inject, onUnmounted, ref, watch } from 'vue';

import { ClientServiceContainer } from '../services/ClientServiceContainer';

const services = inject<ClientServiceContainer>('services')!;

// Reactive state for health tracking
const currentHealth = ref(0);
const maxHealth = ref(0);
const isVisible = ref(false);

/**
 * Handle for the requestAnimationFrame loop.
 * Using RAF for smooth polling of ECS health data, as the health component
 * may change frequently during combat and doesn't trigger standard Vue reactivity.
 */
let frameId: number | null = null;

/**
 * Updates the local reactive state from the ECS world.
 * This runs every frame to ensure the HUD is always in sync with the game state.
 */
const updateHealth = () => {
  const world = services.clientWorld.getWorld();
  const playerEntityId = services.localPlayer.entityId;

  if (playerEntityId !== undefined) {
    const health = world.getComponent(playerEntityId, 'Health');
    if (health) {
      currentHealth.value = health.current;
      maxHealth.value = health.max;
      isVisible.value = true;
    } else {
      // Hide if the player exists but health component is missing (unexpected state)
      isVisible.value = false;
    }
    // Continue loop as long as the player entity exists
    frameId = requestAnimationFrame(updateHealth);
  } else {
    // Stop the loop if the player entity is missing (e.g., during scene transition)
    isVisible.value = false;
    frameId = null;
  }
};

/**
 * Watch for the local player entity ID to start/stop the polling loop.
 * This ensures we don't waste CPU cycles polling the ECS before the player
 * has spawned or while the game is loading.
 */
watch(
  () => services.localPlayer.entityId,
  (newId) => {
    if (newId !== undefined) {
      // Start the loop if not already running
      if (frameId === null) {
        frameId = requestAnimationFrame(updateHealth);
      }
    } else {
      // Stop the loop and reset visibility when player is gone
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
        frameId = null;
      }
      isVisible.value = false;
    }
  },
  { immediate: true },
);

/**
 * Calculates health percentage for the progress bar fill.
 * Clamped between 0-100 to prevent visual overflow for negative health or overhealing.
 */
const healthPercentage = computed(() => {
  if (maxHealth.value <= 0) return 0;
  return Math.max(0, Math.min(100, (currentHealth.value / maxHealth.value) * 100));
});

onUnmounted(() => {
  if (frameId !== null) {
    cancelAnimationFrame(frameId);
  }
});
</script>

<template>
  <div
    v-if="isVisible"
    class="hud-container"
  >
    <div class="health-bar-wrapper">
      <div class="health-bar-background">
        <div 
          class="health-bar-fill" 
          :style="{ width: healthPercentage + '%' }"
        >
          <div class="health-bar-glint" />
        </div>
      </div>
      <div class="health-text">
        <span class="health-label">HP</span>
        <span class="health-values">{{ Math.max(0, currentHealth) }} / {{ maxHealth }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.hud-container {
  position: fixed;
  bottom: 24px;
  left: 24px;
  pointer-events: none;
  z-index: 1000;
  font-family: 'Inter', system-ui, sans-serif;
  user-select: none;
}

.health-bar-wrapper {
  position: relative;
  width: 280px;
  padding: 4px;
  background: rgba(0, 0, 0, 0.6);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  backdrop-filter: blur(8px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.health-bar-background {
  height: 20px;
  background: rgba(40, 0, 0, 0.8);
  border-radius: 4px;
  overflow: hidden;
  position: relative;
  border: 1px solid rgba(0, 0, 0, 0.5);
}

.health-bar-fill {
  height: 100%;
  background: linear-gradient(to bottom, #ff3333, #aa0000);
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  box-shadow: inset 0 1px 1px rgba(255, 255, 255, 0.3);
}

.health-bar-glint {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 40%;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.2), transparent);
}

.health-text {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 4px;
  color: #fff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
}

.health-label {
  font-size: 10px;
  font-weight: 800;
  color: #ff9999;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.health-values {
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: -0.5px;
}
</style>
